import React, { useEffect, useMemo } from "react";
import { Button, DeleteIcon, PlusIcon, Select } from "@pd/pd-ui-kit";
import {
  Control,
  Controller,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import Modal from "~/shared/ui/Fields/Modal";
import { useGetDealersForSelect } from "~/entity/Dealers/hooks/useGetDealersForSelect";
import { useGetExpectationsForSelect } from "~/entity/Employee";
import { ILicenseRecalculateParams } from "~/entity/Licenses";
import { useGetDealerEmployees } from "~/entity/Users/hooks/useGetDealerEmployees";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";

import s from "~/pages/LicenseManagement/LicenseManagement.less";

interface ISelectValue {
  value: string;
  label: string;
}

interface IRecalculateEmployeeRow {
  dealer: ISelectValue | null;
  employee: ISelectValue | null;
}

interface ILicenseRecalculateForm {
  dealers: ISelectValue[];
  positions: ISelectValue[];
  employees: IRecalculateEmployeeRow[];
}

interface LicenseRecalculateModalProps {
  onSubmit: (params: ILicenseRecalculateParams) => void;
}

const defaultEmployeeRow: IRecalculateEmployeeRow = {
  dealer: null,
  employee: null,
};

const buildDefaultValues = (): ILicenseRecalculateForm => ({
  dealers: [],
  positions: [],
  employees: [{ ...defaultEmployeeRow }],
});

const toIds = (items?: ISelectValue[] | null): number[] | undefined => {
  const ids = (items || [])
    .map((item) => Number(item?.value))
    .filter((id) => Number.isFinite(id) && id > 0);

  return ids.length ? ids : undefined;
};

const RecalculateEmployeeRow = ({
  control,
  index,
  onRemove,
}: {
  control: Control<ILicenseRecalculateForm>;
  index: number;
  onRemove?: () => void;
}) => {
  const { t } = useTranslation();
  const dealer = useWatch({
    control,
    name: `employees.${index}.dealer`,
  }) as ISelectValue | null;
  const { data: employees, isLoading } = useGetDealerEmployees(
    dealer?.value || undefined,
  );

  const {
    options: dealerOptions,
    loading: dealersLoading,
    onSearch: onDealerSearch,
    onScrollBottom: onDealerScrollBottom,
    onOpenChange: onDealerOpenChange,
  } = useGetDealersForSelect();

  const dealerOptionsWithSelected =
    dealer && !dealerOptions.some((option) => option.value === dealer.value)
      ? [dealer, ...dealerOptions]
      : dealerOptions;

  const employeeOptions = useMemo(
    () =>
      (employees || []).map((employee) => ({
        value: String(employee.id),
        label: employee.fullName,
      })),
    [employees],
  );

  const handleDealerPopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;

    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
      onDealerScrollBottom();
    }
  };

  return (
    <div className={s.modalRow}>
      <div className={s.modalField}>
        <Controller
          name={`employees.${index}.dealer`}
          control={control}
          render={({ onChange, value }) => (
            <Select
              labelInValue
              allowClear
              showSearch
              filterOption={false}
              value={value}
              onChange={onChange}
              options={dealerOptionsWithSelected}
              loading={dealersLoading}
              onSearch={onDealerSearch}
              onOpenChange={onDealerOpenChange}
              onPopupScroll={handleDealerPopupScroll}
              placeholder={t("licenseManagement.recalculateModal.dealer")}
            />
          )}
        />
      </div>
      <div className={s.modalField}>
        <Controller
          name={`employees.${index}.employee`}
          control={control}
          render={({ onChange, value }) => (
            <Select
              labelInValue
              allowClear
              showSearch
              filterOption={false}
              value={value}
              onChange={onChange}
              options={employeeOptions}
              loading={isLoading}
              disabled={!dealer?.value}
              placeholder={t("licenseManagement.recalculateModal.employeeName")}
            />
          )}
        />
      </div>
      {onRemove && (
        <Button
          variant="text"
          color="secondary"
          size="small"
          htmlType="button"
          icon={<DeleteIcon size={16} />}
          aria-label={t("ui.delete")}
          title={t("ui.delete")}
          onClick={onRemove}
        />
      )}
    </div>
  );
};

const LicenseRecalculateModal = ({
  onSubmit,
}: LicenseRecalculateModalProps) => {
  const { t } = useTranslation();
  const { openModal } = useURLParamsGetter<{ openModal: string }>();
  const setParams = useURLParamsSetter();
  const isOpen = openModal === "Recalculate";

  const {
    options: dealerOptions,
    loading: dealersLoading,
    onSearch: onDealerSearch,
    onScrollBottom: onDealerScrollBottom,
    onOpenChange: onDealerOpenChange,
  } = useGetDealersForSelect();

  const {
    options: positionOptions,
    loading: positionsLoading,
    onSearch: onPositionSearch,
    onOpenChange: onPositionOpenChange,
  } = useGetExpectationsForSelect();

  const { control, handleSubmit, reset } = useForm<ILicenseRecalculateForm>({
    defaultValues: useMemo(buildDefaultValues, []),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "employees",
  });

  useEffect(() => {
    if (!isOpen) {
      reset(buildDefaultValues());
    }
  }, [isOpen, reset]);

  const closeModal = () =>
    setParams({ openModal: undefined, currentId: undefined });

  const handleDealerPopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;

    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
      onDealerScrollBottom();
    }
  };

  const submit = handleSubmit((data) => {
    onSubmit({
      dealerIds: toIds(data.dealers),
      instanceIds: toIds(data.positions),
      employeeIds: toIds(
        data.employees
          .map((row) => row.employee)
          .filter(Boolean) as ISelectValue[],
      ),
    });
    closeModal();
  });

  return (
    <Modal
      isOpen={isOpen}
      title={t("licenseManagement.recalculateModal.title")}
      actionClose={closeModal}
      w={948}
    >
      <div className={s.modalForm}>
        <div className={s.modalField}>
          <span className={s.modalLabel}>
            {t("licenseManagement.recalculateModal.dealers")}
          </span>
          <Controller
            name="dealers"
            control={control}
            render={({ onChange, value }) => (
              <Select
                mode="multiple"
                labelInValue
                allowClear
                showSearch
                filterOption={false}
                value={value}
                onChange={onChange}
                options={dealerOptions}
                loading={dealersLoading}
                onSearch={onDealerSearch}
                onOpenChange={onDealerOpenChange}
                onPopupScroll={handleDealerPopupScroll}
                placeholder={t("licenseManagement.modal.notSelected")}
              />
            )}
          />
        </div>

        <div className={s.modalField}>
          <span className={s.modalLabel}>
            {t("licenseManagement.recalculateModal.position")}
          </span>
          <Controller
            name="positions"
            control={control}
            render={({ onChange, value }) => (
              <Select
                mode="multiple"
                labelInValue
                allowClear
                showSearch
                filterOption={false}
                value={value}
                onChange={onChange}
                options={positionOptions}
                loading={positionsLoading}
                onSearch={onPositionSearch}
                onOpenChange={onPositionOpenChange}
                placeholder={t("licenseManagement.modal.notSelected")}
              />
            )}
          />
        </div>

        <div className={s.modalRows}>
          <span className={s.modalLabel}>
            {t("licenseManagement.recalculateModal.employee")}
          </span>
          {fields.map((field, index) => (
            <RecalculateEmployeeRow
              key={field.id}
              control={control}
              index={index}
              onRemove={fields.length >= 2 ? () => remove(index) : undefined}
            />
          ))}
          <Button
            variant="text"
            color="primary"
            fontSize="12"
            size="small"
            htmlType="button"
            icon={<PlusIcon size={16} />}
            onClick={() => append({ ...defaultEmployeeRow })}
          >
            {t("licenseManagement.recalculateModal.addEmployee")}
          </Button>
        </div>

        <div className={s.modalFooter}>
          <div className={s.divider} />
          <div className={s.modalButtons}>
            <Button
              bold
              color="secondary"
              fontSize="14"
              size="medium"
              radius={5}
              onClick={closeModal}
            >
              {t("licenseManagement.modal.close")}
            </Button>
            <Button
              bold
              color="primary"
              fontSize="14"
              size="medium"
              radius={5}
              onClick={submit}
            >
              {t("licenseManagement.modal.save")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LicenseRecalculateModal;
